import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import axiosInstance from "../../utils/axiosConfig";
import { compactParams } from "./params";

export type ApiQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
> = {
  queryKey: TQueryKey;
  /** Path relative to `REACT_APP_API_URL` / axios `baseURL` (e.g. `api/users`). */
  url: string;
  params?: Record<string, string | number | boolean | null | undefined>;
  axiosConfig?: Pick<AxiosRequestConfig, "headers" | "timeout">;
} & Omit<
  UseQueryOptions<TQueryFnData, AxiosError, TData, TQueryKey>,
  "queryFn" | "queryKey"
>;

/**
 * GET request wired to the shared axios instance (session cookie + 401 redirect).
 *
 * @example
 * const { data, isPending } = useApiQuery({
 *   queryKey: apiQueryKey("users", { page, limit }),
 *   url: "api/users",
 *   params: { page, limit: 25 },
 *   enabled: Boolean(isAuthenticated),
 * });
 */
export function useApiQuery<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  options: ApiQueryOptions<TQueryFnData, TData, TQueryKey>
): UseQueryResult<TData, AxiosError> {
  const { queryKey, url, params, axiosConfig, ...queryOptions } = options;

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<TQueryFnData>(url, {
        params: compactParams(params),
        ...axiosConfig,
        signal,
      });
      return data;
    },
    ...queryOptions,
  });
}
